import React, { useState, useMemo } from "react";
import { formatDateStr } from "./calendarUtils";
import { getBookingBlocksForDay } from "./availabilityUtils";
import { Room } from "../../types/room";
import { Booking } from "../../types/booking";
import { User } from "../../types/auth";
import { EditBookingModal, BookingWithExtras } from "../../pages/BookingsPage";
import { districts } from "../../constants/districts";

// ── Palette ────────────────────────────────────────────────────────────────────
const ROOM_COLORS: string[] = [
  "#EF4444", // red
  "#3B82F6", // blue
  "#C07850", // coffee
  "#10B981", // green
  "#F59E0B", // orange
  "#EC4899", // pink
  "#06B6D4", // cyan
  "#84CC16", // lime
];

const DAY_SHORT: string[] = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];

interface DayEntry {
  date: Date;
  dateStr: string;
}

interface RoomOverviewTableProps {
  rooms: Room[];
  allBookings: Booking[];
  onSelectRoom: (room: Room) => void;
  currentUser: User | null;
  onBookingUpdated: () => void;
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function RoomOverviewTable({ rooms, allBookings, onSelectRoom, currentUser, onBookingUpdated }: RoomOverviewTableProps): React.ReactElement {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const todayStr = formatDateStr(today);

  const [viewDays, setViewDays] = useState<number>(7);
  const [startDate, setStartDate] = useState<Date>(today);
  const [districtFilter, setDistrictFilter] = useState<string>("all");
  const [editingBooking, setEditingBooking] = useState<BookingWithExtras | null>(null);

  // ── Filtered rooms ─────────────────────────────────────────────────────────
  const filteredRooms = useMemo<Room[]>(() => {
    if (districtFilter === "all") return rooms;
    return rooms.filter((r) => r.district === districtFilter);
  }, [rooms, districtFilter]);

  // ── Days in view ───────────────────────────────────────────────────────────
  const days = useMemo<DayEntry[]>(
    () =>
      Array.from({ length: viewDays }, (_, i) => {
        const d = addDays(startDate, i);
        return { date: d, dateStr: formatDateStr(d) };
      }),
    [startDate, viewDays]
  );

  // ── Navigation ─────────────────────────────────────────────────────────────
  const goBack = (): void => setStartDate((d) => addDays(d, -viewDays));
  const goForward = (): void => setStartDate((d) => addDays(d, viewDays));
  const goToday = (): void => setStartDate(today);

  // ── Range label ────────────────────────────────────────────────────────────
  const rangeLabel = useMemo<string>(() => {
    const s = days[0]?.date;
    const e = days[days.length - 1]?.date;
    if (!s || !e) return "";
    if (viewDays === 1)
      return s.toLocaleDateString("vi-VN", {
        weekday: "long",
        day: "numeric",
        month: "numeric",
        year: "numeric",
      });
    return `thg ${s.getMonth() + 1} ${s.getDate()} – thg ${e.getMonth() + 1} ${e.getDate()}, ${e.getFullYear()}`;
  }, [days, viewDays]);

  // ── Room color map ─────────────────────────────────────────────────────────
  const colorOf = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    rooms.forEach((r, i) => { map[r._id] = ROOM_COLORS[i % ROOM_COLORS.length]; });
    return map;
  }, [rooms]);

  return (
    <div className="space-y-4">
      {/* Header + Navigation */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">Lịch đặt phòng</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Xem tất cả phòng cùng lúc — phân biệt bằng màu</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Prev / Today / Next */}
          <div className="flex items-center gap-1">
            <button
              onClick={goBack}
              className="w-8 h-8 rounded-lg border border-zinc-200 flex items-center justify-center text-zinc-600 hover:bg-zinc-100 transition-colors text-lg leading-none"
            >
              ‹
            </button>
            <button
              onClick={goToday}
              className="px-3 py-1.5 bg-coffee-600 text-white text-sm rounded-lg font-medium hover:bg-coffee-700 transition-colors"
            >
              Hôm nay
            </button>
            <button
              onClick={goForward}
              className="w-8 h-8 rounded-lg border border-zinc-200 flex items-center justify-center text-zinc-600 hover:bg-zinc-100 transition-colors text-lg leading-none"
            >
              ›
            </button>
          </div>

          <span className="text-sm font-semibold text-zinc-700 px-1">{rangeLabel}</span>

          {/* Lọc theo quận */}
          <select
            value={districtFilter}
            onChange={(e) => setDistrictFilter(e.target.value)}
            className="px-3 py-1.5 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-coffee-400 bg-white text-zinc-700"
          >
            <option value="all">Tất cả quận/huyện</option>
            {districts.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>

          {/* View mode */}
          <div className="flex rounded-lg overflow-hidden border border-zinc-200 bg-white text-sm font-medium">
            {[1, 3, 5, 7].map((n) => (
              <button
                key={n}
                onClick={() => setViewDays(n)}
                className={`px-3 py-1.5 transition-colors ${
                  viewDays === n ? "bg-coffee-600 text-white" : "text-zinc-500 hover:text-zinc-800 hover:bg-zinc-50"
                }`}
              >
                {n === 1 ? "1 ngày" : `${n}d`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Gantt grid */}
      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <div style={{ minWidth: Math.max(filteredRooms.length ? viewDays * 150 + 176 : 400, 400) }}>

            {/* Header row */}
            <div className="flex border-b border-zinc-100 bg-zinc-50">
              <div className="w-44 flex-shrink-0 border-r border-zinc-100 px-4 py-3">
                <span className="text-xs font-medium text-zinc-400 tracking-widest uppercase">Phòng</span>
              </div>
              {days.map(({ date, dateStr }) => {
                const isToday = dateStr === todayStr;
                return (
                  <div
                    key={dateStr}
                    className={`flex-1 py-3 text-center border-r border-zinc-100 last:border-r-0 ${
                      isToday ? "bg-coffee-50" : ""
                    }`}
                  >
                    <div className={`text-[11px] font-bold uppercase tracking-wide ${isToday ? "text-coffee-600" : "text-zinc-400"}`}>
                      {DAY_SHORT[date.getDay()]}
                    </div>
                    <div className={`text-2xl font-bold leading-tight ${isToday ? "text-coffee-600" : "text-zinc-800"}`}>
                      {date.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Room rows */}
            {filteredRooms.length === 0 && (
              <div className="py-12 text-center text-zinc-400 text-sm">Không có phòng nào</div>
            )}
            {filteredRooms.map((room) => {
              const color = colorOf[room._id];
              const roomBookings = allBookings.filter(
                (b) => b.roomId === room._id || (b.roomId as { _id: string })?._id === room._id
              );
              return (
                <div
                  key={room._id}
                  className="flex border-b border-zinc-50 last:border-b-0 hover:bg-zinc-50/60 transition-colors"
                >
                  {/* Room label */}
                  <div
                    className="w-44 flex-shrink-0 border-r border-zinc-100 px-4 py-4 cursor-pointer"
                    onClick={() => onSelectRoom(room)}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: color }}
                      />
                      <div className="min-w-0">
                        <p className="font-semibold text-zinc-900 text-sm leading-tight truncate">{room.name}</p>
                        <p className="text-xs text-zinc-400 mt-0.5 truncate">{room.district}</p>
                      </div>
                    </div>
                  </div>

                  {/* Day cells */}
                  {days.map(({ dateStr }) => {
                    const isToday = dateStr === todayStr;
                    const blocks = getBookingBlocksForDay(dateStr, roomBookings);
                    const hasAny = blocks.length > 0;

                    return (
                      <div
                        key={dateStr}
                        className={`flex-1 relative border-r border-zinc-50 last:border-r-0 ${
                          isToday ? "bg-coffee-50/30" : ""
                        }`}
                        style={{ height: 72 }}
                      >
                        {!hasAny ? (
                          <div className="flex items-center justify-center h-full pb-3">
                            <span className="text-xs text-zinc-200">Trống</span>
                          </div>
                        ) : (
                          <div className="absolute inset-x-1 top-3 bottom-5">
                            {blocks.map((block, i) => {
                              const leftPct = (block.startHour / 24) * 100;
                              const widthPct = Math.max(
                                ((block.endHour - block.startHour) / 24) * 100,
                                4
                              );
                              if (block.isCleaning) {
                                return (
                                  <div
                                    key={i}
                                    className="absolute top-0 bottom-0 rounded"
                                    style={{
                                      left: `${leftPct}%`,
                                      width: `${widthPct}%`,
                                      background:
                                        "repeating-linear-gradient(45deg,#e4e4e7,#e4e4e7 3px,#d4d4d8 3px,#d4d4d8 6px)",
                                    }}
                                  />
                                );
                              }
                              const isLld = block.booking.source === "lld" || block.booking.source === "LLD";
                              const isLocked = block.booking.source === "block";
                              return (
                                <div
                                  key={i}
                                  className="group absolute top-0 bottom-0 rounded flex items-center justify-center overflow-visible cursor-pointer"
                                  style={{
                                    left: `${leftPct}%`,
                                    width: `${widthPct}%`,
                                    backgroundColor: isLocked ? "#71717a" : color,
                                    opacity: 0.85,
                                  }}
                                  onDoubleClick={(e) => {
                                    e.stopPropagation();
                                    if (!isLocked) setEditingBooking(block.booking as BookingWithExtras);
                                  }}
                                >
                                  {isLocked
                                    ? <span className="text-white text-[10px] leading-none">🔒</span>
                                    : <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="white" stroke="none"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}

                                  {/* Hover tooltip: tên khách + nguồn (LLD/Web/Khóa) */}
                                  <div className="hidden group-hover:flex flex-col items-start gap-1 absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-20 whitespace-nowrap bg-zinc-900 text-white text-xs rounded-lg px-2.5 py-1.5 shadow-lg pointer-events-none">
                                    <span className="font-semibold">{isLocked ? (block.booking.notes || "Khóa phòng") : (block.booking.guestName || "Khách")}</span>
                                    <span className="text-zinc-300">{block.booking.checkInTime} – {block.booking.checkOutTime}</span>
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${isLocked ? "bg-zinc-400 text-zinc-900" : isLld ? "bg-amber-400 text-amber-900" : "bg-sky-400 text-sky-900"}`}>
                                      {isLocked ? "🔒 Khóa phòng" : isLld ? "LLD" : "Web"}
                                    </span>
                                    <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900" />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Time axis ticks */}
                        <div className="absolute bottom-0.5 left-0 right-0 flex justify-between px-1 pointer-events-none">
                          {[0, 6, 12, 18].map((h) => (
                            <span key={h} className="text-[9px] text-zinc-300 leading-none">
                              {h}h
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        {rooms.length > 0 && (
          <div className="px-6 py-3 border-t border-zinc-100 flex flex-wrap gap-x-5 gap-y-1.5 justify-center">
            {rooms.map((room) => (
              <span key={room._id} className="flex items-center gap-1.5 text-xs text-zinc-500">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: colorOf[room._id] }}
                />
                {room.name}
              </span>
            ))}
            <span className="flex items-center gap-1.5 text-xs text-zinc-400">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="#a1a1aa" stroke="none"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Đã đặt
            </span>
          </div>
        )}
      </div>

      {editingBooking && (
        <EditBookingModal
          booking={editingBooking}
          currentUser={currentUser}
          onClose={() => setEditingBooking(null)}
          onSaved={() => { setEditingBooking(null); onBookingUpdated(); }}
        />
      )}
    </div>
  );
}
